/**
 * Centralized PT-BR copy for the staff-onboarding feature.
 *
 * admin-web today keeps strings inline per component; this feature
 * centralizes them (per the task convention "strings PT-BR centralizadas")
 * so the wizard's many labels/notices live in one auditable place. Scoped
 * to the feature — not a global i18n layer.
 */
import type {
  BusinessType,
  DispatchMode,
  InviteDeliveryStatus,
  IsoWeekday,
  MerchantStatus,
} from './types';

export const businessTypeLabel: Record<BusinessType, string> = {
  RESTAURANT: 'Restaurante',
  MARKET: 'Mercado',
  PHARMACY: 'Farmácia',
  PET_SHOP: 'Pet Shop',
  CONVENIENCE: 'Conveniência',
  OTHER: 'Outro',
};

export const dispatchModeLabel: Record<DispatchMode, string> = {
  OWN_COURIERS: 'Entregadores próprios',
  GLOBAL_POOL: 'Pool global da plataforma',
};

export const merchantStatusLabel: Record<MerchantStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  SUSPENDED: 'Suspenso',
  PENDING_OWNER_ACTIVATION: 'Aguardando ativação',
};

export const inviteDeliveryStatusLabel: Record<InviteDeliveryStatus, string> = {
  PENDING: 'Convite pendente',
  EMAIL_SENT: 'E-mail enviado',
  SMS_SENT: 'SMS enviado',
  BOTH_SENT: 'E-mail + SMS enviados',
  FAILED: 'Falha no envio',
};

export const weekdayLabel: Record<IsoWeekday, string> = {
  1: 'Segunda',
  2: 'Terça',
  3: 'Quarta',
  4: 'Quinta',
  5: 'Sexta',
  6: 'Sábado',
  7: 'Domingo',
};

export const strings = {
  nav: { label: 'Onboarding' },

  dashboard: {
    title: 'Onboarding de comerciantes',
    subtitle: 'Cadastre lojas em campo e acompanhe a ativação dos proprietários.',
    newMerchant: 'Comerciante + comércio',
    newStore: 'Novo comércio (dono existente)',
    searchLabel: 'Buscar',
    searchPlaceholder: 'Buscar por nome…',
    statusFilter: 'Status',
    allStatuses: 'Todos os status',
    empty: 'Nenhum comércio encontrado.',
    loadError: 'Não foi possível carregar os comércios.',
    columns: {
      name: 'Comércio',
      status: 'Status',
      invite: 'Convite',
      created: 'Criado em',
    },
    // TODO[FAV-127] — lista/detalhe/stats próprios do staff não existem; usando /admin/merchants.
    backendNote: 'Listagem via painel admin enquanto GET /staff/merchants (FAV-127) não existe.',
  },

  metrics: {
    activeStores: 'Comércios ativos',
    approvedThisMonth: 'Aprovados/mês',
    totalOrders: 'Pedidos total',
    activeCategories: 'Categorias ativas',
    pendingActivation: 'Aguardando ativação',
    unavailable: '—',
    // TODO[FAV-127] — métrica exata depende de GET /staff/me/stats.
    unavailableHint: 'Métrica exata via GET /staff/me/stats (FAV-127).',
  },

  wizard: {
    title: 'Comerciante + comércio',
    subtitle: 'Cadastro do dono e da loja. O proprietário ativa pelo convite.',
    steps: ['Estabelecimento', 'Responsável', 'Endereço', 'Fotos', 'Operação', 'Revisão'],
    back: 'Voltar',
    next: 'Avançar',
    submit: 'Cadastrar e enviar convite',
    cancel: 'Cancelar',
  },

  establishment: {
    legend: 'Dados do estabelecimento',
    tradeName: 'Nome fantasia',
    tradeNamePlaceholder: 'Ex.: Lanchonete da Praça',
    legalName: 'Razão social',
    cnpj: 'CNPJ / MEI',
    businessType: 'Segmento',
    category: 'Categoria',
    categoryPlaceholder: 'Selecione',
    description: 'Descrição',
    phone: 'Telefone do comércio',
  },

  responsible: {
    legend: 'Responsável (proprietário)',
    fullName: 'Nome completo',
    cpf: 'CPF',
    phone: 'Telefone',
    email: 'E-mail',
    tempPassword: 'Senha temporária',
    tempPasswordRepeat: 'Repetir senha temporária',
    tempPasswordHint: '12+ caracteres com maiúscula, minúscula, número e símbolo. O dono troca no 1º acesso.',
    tempPasswordNotice:
      'Defina uma senha temporária com o comerciante presente — ele já consegue acessar e troca no primeiro login. Ao concluir, você também recebe um link de convite para enviar ao dono.',
    pix: 'Chave PIX (destino de repasse)',
    // TODO[FAV-126] — Merchant.pixKey ainda não existe no backend.
    pixSoon: 'Em breve',
    pixSoonHint: 'Campo disponível quando o backend adicionar Merchant.pixKey (FAV-126).',
  },

  address: {
    legend: 'Endereço',
    zip: 'CEP',
    zipSearch: 'Buscar',
    number: 'Número',
    street: 'Rua / logradouro',
    complement: 'Complemento',
    neighborhood: 'Bairro',
    city: 'Cidade',
    state: 'UF',
    notFound: 'CEP não encontrado',
  },

  media: {
    legend: 'Fotos',
    logo: 'Logo',
    logoRequired: 'O logo é obrigatório.',
    cover: 'Foto de capa',
    // TODO[FAV-112]/OQ-3 — storefront aceito mas ignorado no V1.
    coverHint: 'Opcional. Ainda não persistida no V1 (somente o logo é salvo).',
    ownerDoc: 'Documento do dono (RG/CNH)',
    // TODO[FAV-112] — staff create não tem campo de documento do dono.
    ownerDocHint: 'Opcional. Será persistido quando o backend incluir documentos no cadastro por staff (FAV-112).',
  },

  operation: {
    legend: 'Operação',
    hours: 'Horários de funcionamento',
    /** Day-row toggle label (FAV-169: marking a day means it's OPEN, not closed). */
    openToggle: 'Aberto',
    closed: 'Fechado',
    open: 'Abre',
    close: 'Fecha',
    applyToOpenDays: 'Aplicar a todos os dias abertos',
    minimumOrder: 'Pedido mínimo (R$)',
    prepTime: 'Tempo de preparo (min)',
    dispatchMode: 'Modo de despacho',
    autoAccept: 'Aceitar pedidos automaticamente',
  },

  review: {
    legend: 'Revisão',
    establishment: 'Estabelecimento',
    responsible: 'Responsável',
    address: 'Endereço',
    operation: 'Operação',
    confirm: 'Confira os dados antes de enviar o convite.',
  },

  forOwner: {
    title: 'Novo comércio (dono existente)',
    subtitle: 'Cadastre uma nova loja para um comerciante já existente.',
    steps: ['Comerciante', 'Estabelecimento', 'Endereço', 'Operação', 'Revisão'],
    owner: 'Comerciante existente',
    ownerPlaceholder: 'Selecione o comerciante',
    ownerRequired: 'Selecione o comerciante',
    autoApprove: 'Aprovar comércio automaticamente (status = APPROVED)',
    submit: 'Cadastrar comércio',
    // TODO[FAV-128] — staff for-owner não existe; usando /admin/merchants/for-owner.
    backendNote:
      'Usando POST /admin/merchants/for-owner (requer admin) enquanto o endpoint staff (FAV-128) não existe.',
  },

  detail: {
    backToList: 'Voltar para a lista',
    notFound: 'Comércio não encontrado.',
    sectionInvite: 'Convite de ativação',
    sectionStore: 'Dados do comércio',
    resendInvite: 'Reenviar convite',
    archive: 'Arquivar cadastro',
    archiveConfirmTitle: 'Arquivar cadastro?',
    archiveConfirmBody: 'A loja será marcada como REJEITADA e o convite invalidado. Ação de admin.',
    archiveReason: 'Motivo (opcional)',
    inviteSentAt: 'Convite enviado em',
    inviteExpiresAt: 'Convite expira em',
    inviteAccepted: 'Ativado pelo dono',
    resendCount: 'Reenvios',
    // TODO[FAV-127] — campos de convite só existem em GET /staff/merchants/{id}.
    inviteUnavailable: 'Dados do convite indisponíveis até GET /staff/merchants/{id} (FAV-127).',
  },

  toast: {
    created: 'Comércio cadastrado e convite enviado',
    createError: 'Falha ao cadastrar o comércio',
    inviteResent: 'Convite reenviado',
    inviteResendError: 'Falha ao reenviar o convite',
    archived: 'Cadastro arquivado',
    archiveError: 'Falha ao arquivar o cadastro',
    forOwnerCreated: 'Comércio cadastrado para o dono existente',
    forOwnerError: 'Falha ao cadastrar o comércio',
  },

  validation: {
    required: 'Campo obrigatório',
    tradeName: 'Nome fantasia obrigatório',
    cnpj: 'CNPJ inválido',
    businessType: 'Segmento obrigatório',
    category: 'Categoria obrigatória',
    ownerName: 'Nome do responsável obrigatório',
    ownerNameFormat: 'Use apenas letras, espaços, hífen e apóstrofo (sem números)',
    cpf: 'CPF inválido',
    email: 'E-mail inválido',
    phone: 'Telefone obrigatório',
    phoneFormat: 'Informe um celular com DDD + 9 dígitos (ex.: (21) 99999-9999)',
    password: 'Senha deve ter 12+ caracteres com maiúscula, minúscula, número e símbolo',
    pixKey: 'Chave PIX inválida para o tipo informado',
    pixKeyRequired: 'Chave PIX obrigatória',
    pixKeyTypeRequired: 'Selecione o tipo da chave PIX',
    passwordMismatch: 'As senhas não coincidem',
    zip: 'CEP inválido',
    street: 'Rua obrigatória',
    number: 'Número obrigatório',
    neighborhood: 'Bairro obrigatório',
    city: 'Cidade obrigatória',
    state: 'UF obrigatória',
    logo: 'Logo obrigatório',
    hours: 'Defina ao menos um dia de funcionamento.',
  },

  inviteSentTo: (email?: string | null, phone?: string | null): string => {
    const parts = [email, phone].filter((p): p is string => Boolean(p));
    return parts.length > 0 ? `Convite enviado para ${parts.join(' e ')}` : 'Convite gerado';
  },
} as const;
